import { type ControlState, NO_CONTROLS } from "../../sim";
import { el } from "../../ui/dom";
import type { Action } from "./bindings";

/** When the on-screen controls show: on touch screens (and after a touch), always, or never. */
export type TouchMode = "auto" | "on" | "off";

/** Held on-screen buttons → controls. Pure (unit-tested). */
export function controlsFromTouch(held: ReadonlySet<Action>): ControlState {
	if (held.size === 0) return { ...NO_CONTROLS };
	return {
		thrust: held.has("thrust"),
		left: held.has("left"),
		right: held.has("right"),
		fireLaser: held.has("fireLaser"),
		fireMissile: held.has("fireMissile"),
	};
}

/** Auto mode: a coarse pointer (phone, tablet) means touch controls from the start. */
export function prefersTouch(): boolean {
	return typeof matchMedia === "function" && matchMedia("(pointer: coarse)").matches;
}

const BUTTONS: ReadonlyArray<{
	action: Action;
	label: string;
	side: "left" | "right";
	name: string;
}> = [
	{ action: "left", label: "◀", side: "left", name: "Turn left" },
	{ action: "right", label: "▶", side: "left", name: "Turn right" },
	{ action: "thrust", label: "▲", side: "left", name: "Thrust" },
	{ action: "fireMissile", label: "M", side: "right", name: "Fire missile" },
	{ action: "fireLaser", label: "L", side: "right", name: "Fire laser" },
];

/**
 * On-screen controls for one local pilot (P10.2): steering and thrust under the left thumb,
 * weapons under the right, plus a pause button. Multi-touch: every button tracks its own
 * pointers, so thrust + turn + fire can be held at once. In auto mode it appears with the
 * first touch and hides again when a key is pressed (a keyboard or pad player doesn't need it).
 */
export class TouchControls {
	readonly el: HTMLElement;
	private readonly pointers = new Map<Action, Set<number>>();
	private readonly held = new Set<Action>();
	private visible: boolean;

	constructor(parent: HTMLElement, mode: TouchMode, onPause: () => void) {
		const pad = (side: "left" | "right") =>
			el(
				"div",
				{ class: `touch-pad touch-pad-${side}` },
				...BUTTONS.filter((b) => b.side === side).map((b) =>
					this.button(b.action, b.label, b.name),
				),
			);
		const pause = el(
			"button",
			{ class: "touch-pause", type: "button", "aria-label": "Pause" },
			"II",
		);
		pause.addEventListener("pointerdown", (ev) => {
			ev.preventDefault();
			onPause();
		});
		this.el = el("div", { class: "touch-controls" }, pad("left"), pad("right"), pause);
		this.el.addEventListener("contextmenu", (ev) => ev.preventDefault());
		parent.append(this.el);
		this.visible = mode === "on" || (mode === "auto" && prefersTouch());
		this.render();
		if (mode === "auto") {
			window.addEventListener("pointerdown", this.onAnyPointer, true);
			window.addEventListener("keydown", this.onKey, true);
		}
	}

	read(): ControlState {
		return controlsFromTouch(this.held);
	}

	/** Lets go of everything (pause, blur). */
	reset(): void {
		this.pointers.clear();
		this.held.clear();
		for (const b of this.el.querySelectorAll(".touch-btn")) b.classList.remove("held");
	}

	destroy(): void {
		window.removeEventListener("pointerdown", this.onAnyPointer, true);
		window.removeEventListener("keydown", this.onKey, true);
		this.el.remove();
	}

	private button(action: Action, label: string, name: string): HTMLElement {
		const button = el(
			"button",
			{
				class: `touch-btn touch-${action}`,
				type: "button",
				"aria-label": name,
				"data-action": action,
			},
			label,
		);
		const press = (ev: PointerEvent) => {
			ev.preventDefault();
			const set = this.pointers.get(action) ?? new Set<number>();
			set.add(ev.pointerId);
			this.pointers.set(action, set);
			this.held.add(action);
			button.classList.add("held");
		};
		const release = (ev: PointerEvent) => {
			const set = this.pointers.get(action);
			set?.delete(ev.pointerId);
			if (!set || set.size === 0) {
				this.held.delete(action);
				button.classList.remove("held");
			}
		};
		button.addEventListener("pointerdown", press);
		button.addEventListener("pointerup", release);
		button.addEventListener("pointercancel", release);
		button.addEventListener("lostpointercapture", release);
		return button;
	}

	private readonly onAnyPointer = (ev: PointerEvent): void => {
		if (ev.pointerType === "touch" && !this.visible) {
			this.visible = true;
			this.render();
		}
	};

	private readonly onKey = (): void => {
		if (!this.visible) return;
		this.visible = false;
		this.reset();
		this.render();
	};

	private render(): void {
		this.el.hidden = !this.visible;
		document.body.dataset.touch = String(this.visible);
	}
}
